<template>
  <div>
    <!-- Search -->
    <div class="filter-bar">
      <form
        class="tb-form-group flex flex-wrap items-center gap-4"
        @submit.prevent="keywordSearch()"
      >
        <div class="relative min-w-0 flex-1">
          <i class="fas fa-search"></i>
          <input
            v-model="keyword"
            type="text"
            class="tb-input pr-[30px]"
            placeholder="Search"
            :disabled="loading"
            ref="searchInput"
            data-entity="producer-shoppable-searchInput"
          >
          <button
            v-if="keyword"
            class="absolute right-0 top-1/2 -translate-x-[5px] -translate-y-1/2 border-0 bg-transparent"
            type="button"
            data-entity="producer-shoppable-clearSearch"
            @click="clearSearch"
          >
            <span class="fa fa-close"></span>
          </button>
        </div>

        <button
          type="submit"
          :disabled="loading || !keyword"
          class="tb-btn tb-btn-contained medium-em ml-4"
          data-entity="producer-shoppable-searchButton"
        >
          Search
        </button>
      </form>
      <!-- Chip filters -->
      <div class="flex flex-wrap items-center gap-3">
        <div class="flex items-center gap-3">
          <p class="my-0 mr-2 text-[13px] text-producer-slate-textMuted">Gender</p>

          <div class="flex gap-2">
            <label
              v-for="(v, k) in genders"
              class="relative m-0 cursor-pointer text-[13px]"
              :key="k"
            >
              <span
                class="block rounded-[5px] border p-1 text-producer-primary ring-inset"
                :class="chipFilters.includes(v) ? 'border-2 border-producer-primary bg-producer-slate-accentSoft ring-2 ring-producer-primary' : 'border border-[#4D6870] ring-1 ring-[#4D6870]'"
              >
                {{ k }}
              </span>
              <input
                v-model="chipFilters"
                :value="v"
                type="checkbox"
                class="absolute opacity-0"
                :data-test="`producer-shoppable-${k}Filter`"
              >
            </label>
          </div>
        </div>
        <div class="flex items-center gap-3">
          <p class="my-0 mr-2 text-[13px] text-producer-slate-textMuted">Category</p>

          <div class="flex gap-2">
            <label
              v-for="(v, k) in keywordFilters"
              class="relative m-0 cursor-pointer text-[13px]"
              :key="k"
            >
              <span
                class="block rounded-[5px] border p-1 text-producer-primary ring-inset"
                :class="chipFilters.includes(v) ? 'border-2 border-producer-primary bg-producer-slate-accentSoft ring-2 ring-producer-primary' : 'border border-[#4D6870] ring-1 ring-[#4D6870]'"
              >
                {{ k }}
              </span>
              <input
                v-model="chipFilters"
                :value="v"
                type="checkbox"
                class="absolute opacity-0"
                :data-test="`producer-shoppable-${k}Filter`"
              >
            </label>
          </div>
        </div>
        <div class="flex min-w-0 flex-1 items-center fanatics-price-filter">
          <p class="my-0 mr-2 text-[13px] text-producer-slate-textMuted">Price</p>

          <span class="fanatics-price-filter__value">${{ priceLow }}</span>
          <div class="tb-range-slider tb-range-slider--round fanatics-price-filter__slider">
            <input
              class="tb-range-slider__input"
              type="range"
              :min="0"
              :step="25"
              :max="maxPrice"
              v-model.number="priceLow"
              data-entity="producer-shoppable-lowPriceFilter"
            />

            <input
              class="tb-range-slider__input"
              type="range"
              :min="0"
              :step="25"
              :max="maxPrice"
              v-model.number="priceHigh"
              data-entity="producer-shoppable-highPriceFilter"
            />
          </div>
          <span class="fanatics-price-filter__value">${{ priceHigh }}{{ priceHigh === maxPrice ? '+' : '' }}</span>
        </div>
      </div>
    </div>

    <!-- Results -->
    <div
      class="fanatics-results mt-8 max-h-[50vh] overflow-y-auto"
      :class="{ 'min-h-[20rem]': searched || loading }"
    >
      <div
        v-if="loading"
        class="flex min-h-[20rem] items-center justify-center text-center"
      >
        <span class="fas fa-spinner fa-spin"></span>
      </div>

      <template v-if="results.length">
        <p class="text-os-low text-[13px] text-right">{{ this.total }} results found</p>
        <div
          v-for="r in results"
          :key="r.sourceId"
          class="mt-2 flex items-center rounded border border-transparent bg-producer-slate-surface p-2 first:mt-0"
        >
          <img
            :src="r.imageUrl"
            class="my-2 h-14 w-14 object-contain"
            alt=""
          >
          <div class="mr-2 min-w-0 flex-1 px-3">
            <p class="mb-0">{{ r.price }} {{ r.currency }} <span class="ml-2 inline-block">(ID: {{ r.sourceId }})</span> </p>
            <p
              class="flex-1"
            >
              <a
                :href="r.sourceUrl"
                target="_blank"
                class="mr-1"
              >
                <span class="tgb-sr-only">Link to product page</span>
                <span class="fa fa-external-link"></span>
                <span>{{ r.name }}</span>
              </a>
            </p>
          </div>
          <button
            class="tb-btn tb-btn-contained medium-em"
            :class="{
              'high-em tb-btn-contained--override-disabled': savedItemLookup[r.sourceId],
            }"
            :disabled="savedItemLookup[r.sourceId]"
            :data-test="`producer-shoppable-selectProduct-${r.sourceId}`"
            @click="chooseItem(r)"
          >
            {{ savedItemLookup[r.sourceId] ? 'Saved' : 'Select' }}
          </button>
        </div>
        <div v-if="this.total > 10 && this.page < this.pages" class="mt-2 flex w-full">
          <button
            type="submit"
            :disabled="loading"
            class="tb-btn tb-btn-contained medium-em mx-auto"
            data-entity="producer-shoppable-loadMore"
            @click="loadMore"
          >
            Load More
          </button>
        </div>
      </template>

      <div
        v-else-if="noResults"
        class="text-os-low text-[13px] text-center"
      >
        <p class="text-bold">
          No products found.
        </p>
        <p>
          Check your spelling or try filtering for better results.
        </p>
      </div>
      <p
        v-else
        class="text-os-low text-[13px] text-center"
      >Search a product to get started</p>
    </div>
  </div>
</template>

<script>
import { debounce } from 'lodash-es';

import CatalogMixin from '@/js/mixins/shoppable-catalog';

export default {
  name: 'FanaticsCatalog',
  mixins: [CatalogMixin],
  data() {
    return {
      chipFilters: [],
      genders: {
        Men: 'gender:Male',
        Women: 'gender:Female',
      },
      keywordFilters: {
        Jersey: 'keyword:Jersey',
        Hats: 'keyword:Hat',
        Sweatshirts: 'keyword:Sweatshirt',
        'T-Shirts': 'keyword:T-Shirt',
      },
      priceLow: 0,
      priceHigh: 150,
      maxPrice: 150,
    };
  },

  computed: {
    priceRange() {
      if (this.priceHigh === this.maxPrice && this.priceLow === 0) {
        return '';
      } 
      
      if (this.priceHigh === this.maxPrice) {
        return `price:${this.priceLow}-`;
      }

      return `price:${this.priceLow}-${this.priceHigh}`;
    },
    allFilters() {
      return [
        ...this.chipFilters,
        this.priceRange,
      ];
    },
    queryParams() {
      return {
        filters: this.allFilters,
      };
    },
  },

  created() {
    this.store = 'fanatics'; // set data for mixin
  },
  mounted() {
    this.$refs.searchInput.focus();
  },

  watch: {
    allFilters: debounce(function() {
      this.keywordSearch();
    }, 500),
    priceLow(v) {
      if (v > this.priceHigh) {
        this.priceHigh = v;
      }
    },
    priceHigh(v) {
      if (v < this.priceLow) {
        this.priceLow = v;
      }
    },
  },
};
</script>

<style lang="scss">
.fanatics-results {
  scrollbar-color: rgba(165, 165, 165, 0.5) transparent;
  scrollbar-width: thin;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: rgba(165, 165, 165, 0.5);
    border-radius: 9999px;
    border: 2px solid transparent;
    background-clip: padding-box;
  }
}

.fanatics-price-filter {
  &__value {
    flex: 0 0 auto;
    font-size: 0.6875rem;
    line-height: 1;
    color: #a5a5a5;
    white-space: nowrap;
    padding-left: 2px;
  }

  &__slider {
    flex: 1 1 auto;
    min-width: 0;
    padding: 0;
  }
}
</style>
